import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { detectDevice } from '../utils/deviceDetection';

export type UIMode = 'mobile' | 'tablet' | 'desktop';
export type Orientation = 'portrait' | 'landscape';

export const UI_BREAKPOINTS = {
  mobile: { max: 767 },
  tablet: { min: 768, max: 1279 },
  desktop: { min: 1280 }
} as const;

interface UIStore {
  // Current UI mode
  currentMode: UIMode;
  
  // Auto-detect mode based on screen size
  autoMode: boolean;
  
  // Current device orientation
  orientation: Orientation;
  
  // User's manual mode preference (if autoMode is false)
  userPreferredMode?: UIMode;
  
  // Device capabilities
  hasTouch: boolean;
  hasPen: boolean;
  isIPad: boolean;
  screenSize: 'small' | 'medium' | 'large' | 'xlarge';
  
  // Actions
  setMode: (mode: UIMode) => void;
  setAutoMode: (auto: boolean) => void;
  setOrientation: (orientation: Orientation) => void;
  detectAndSetMode: () => void;
}


// Initialize with device detection on first load
const getInitialState = () => {
  if (typeof window !== 'undefined') {
    const deviceInfo = detectDevice();
    return {
      currentMode: deviceInfo.type,
      orientation: deviceInfo.orientation,
      hasTouch: deviceInfo.hasTouch,
      hasPen: deviceInfo.hasPen,
      isIPad: deviceInfo.isIPad,
      screenSize: deviceInfo.screenSize
    };
  }
  return {
    currentMode: 'desktop' as UIMode,
    orientation: 'landscape' as Orientation,
    hasTouch: false,
    hasPen: false,
    isIPad: false,
    screenSize: 'medium' as const
  };
};

const initialState = getInitialState();

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      ...initialState,
      autoMode: true,
      
      setMode: (mode) => {
        set({ 
          currentMode: mode,
          userPreferredMode: mode,
          autoMode: false // Disable auto mode when user manually selects
        });
      },
      
      setAutoMode: (auto) => {
        set({ autoMode: auto });
        if (auto) {
          // If enabling auto mode, detect and set current mode
          get().detectAndSetMode();
        }
      },
      
      setOrientation: (orientation) => {
        set({ orientation });
      },
      
      detectAndSetMode: () => {
        const state = get();
        
        // If auto mode is disabled, use user preference
        if (!state.autoMode && state.userPreferredMode) {
          set({ currentMode: state.userPreferredMode });
          return;
        }
        
        // Use advanced device detection
        const deviceInfo = detectDevice();
        
        set({ 
          currentMode: deviceInfo.type,
          orientation: deviceInfo.orientation,
          hasTouch: deviceInfo.hasTouch,
          hasPen: deviceInfo.hasPen,
          isIPad: deviceInfo.isIPad,
          screenSize: deviceInfo.screenSize
        });
      }
    }),
    {
      name: 'ui-preferences',
      partialize: (state) => ({
        autoMode: state.autoMode,
        userPreferredMode: state.userPreferredMode
      })
    }
  )
);

// Initialize on load
if (typeof window !== 'undefined') {
  // Initial detection
  useUIStore.getState().detectAndSetMode();
  
  // Listen for resize events
  let resizeTimeout: NodeJS.Timeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      useUIStore.getState().detectAndSetMode();
    }, 100);
  });
  
  // Listen for orientation changes
  window.addEventListener('orientationchange', () => {
    setTimeout(() => {
      useUIStore.getState().detectAndSetMode();
    }, 100);
  });
}